import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const imgNexxupOfficialLogo1021 =
  `${process.env.PUBLIC_URL || ''}/assets/nexxup-logo-header.webp`;

const NAV_LINKS = [
  { to: '/', label: 'Home', icon: 'home' },
  { to: '/leaderboard', label: 'Leaderboard', icon: 'leaderboard' },
  { to: '/heatmap', label: 'Heat Map', icon: 'map' },
  { to: '/discover', label: 'Discover', icon: 'discover' },
  { to: '/scout-score', label: 'Scout Score', icon: 'scout' },
  { to: '/hall-of-fame', label: 'Hall of Fame', icon: 'trophy' },
  { to: '/artist-application?type=fan-submission', label: 'Submit an Artist', icon: 'submit' },
];

// Collapsed under one "Info" heading (issue #109) instead of three separate
// top-level links - About/Platform ID/Copyright are related reference pages,
// not things someone taps into as often as Discover or Heat Map.
const INFO_LINKS = [
  { to: '/about', label: 'About Nexxup', icon: 'info' },
  { to: '/help', label: 'Help', icon: 'help' },
  { to: '/copyright', label: 'Copyright', icon: 'copyright' },
  { to: '/privacy', label: 'Privacy Policy', icon: 'privacy' },
  { to: '/terms', label: 'Terms of Service', icon: 'terms' },
];

// Simple monochrome line icons (grayscale, inherit currentColor) replacing the
// previous emoji. Each entry is the inner SVG of a 24x24 stroke icon.
const ICONS = {
  home: <><path d="M4 11l8-7 8 7" /><path d="M6 10v10h12V10" /></>,
  leaderboard: <><path d="M6 20v-6M12 20V9M18 20V4" /></>,
  map: <><path d="M12 21s6-5.5 6-10a6 6 0 10-12 0c0 4.5 6 10 6 10z" /><circle cx="12" cy="11" r="2" /></>,
  discover: <><path d="M4 13v-1a8 8 0 0116 0v1" /><rect x="3" y="13" width="4" height="6.5" rx="1.3" /><rect x="17" y="13" width="4" height="6.5" rx="1.3" /></>,
  scout: <><path d="M12 3l2.35 4.76 5.25.76-3.8 3.7.9 5.23L12 15.9l-4.7 2.47.9-5.23-3.8-3.7 5.25-.76z" /></>,
  submit: <><circle cx="12" cy="12" r="9" /><path d="M12 8.5v7M8.5 12h7" /></>,
  trophy: <><path d="M8 4h8v4a4 4 0 01-8 0V4z" /><path d="M8 5.5H5.5V7a2.5 2.5 0 002.5 2.5M16 5.5h2.5V7A2.5 2.5 0 0116 9.5" /><path d="M12 12v3.5M9.5 20h5M10 20l.4-4.5h3.2l.4 4.5" /></>,
  info: <><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" /></>,
  help: <><circle cx="12" cy="12" r="9" /><path d="M9.6 9.2a2.4 2.4 0 113.2 2.4c-.7.4-1.1.9-1.1 1.6" /><path d="M12 16h.01" /></>,
  copyright: <><circle cx="12" cy="12" r="9" /><path d="M14.6 9.6a3.4 3.4 0 100 4.8" /></>,
  privacy: <><path d="M12 3l7 3v5c0 4.5-3 8.2-7 10-4-1.8-7-5.5-7-10V6l7-3z" /></>,
  terms: <><path d="M7 3h7l4 4v14H7z" /><path d="M14 3v4h4" /><path d="M10 12h5M10 16h5" /></>,
  admin: <><circle cx="12" cy="12" r="2.6" /><path d="M12 3.5v2.2M12 18.3v2.2M20.5 12h-2.2M5.7 12H3.5M18 6l-1.6 1.6M7.6 16.4L6 18M18 18l-1.6-1.6M7.6 7.6L6 6" /></>,
};

function NavIcon({ name, size = 18 }) {
  const inner = ICONS[name];
  if (!inner) return null;
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"
      style={{ color: 'var(--text-secondary)', flexShrink: 0, display: 'block' }}
      aria-hidden="true"
    >
      {inner}
    </svg>
  );
}

function MobileMenu({ isOpen, onClose, isAdmin, user, getAccountPath }) {
  const [showInfo, setShowInfo] = useState(false);
  const linkStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    color: 'var(--text-primary)',
    textDecoration: 'none',
    fontSize: '16px',
    fontWeight: '500',
    padding: '14px 20px',
    borderBottom: '1px solid var(--border-color)',
    transition: 'background 0.15s ease',
  };

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9998
          }}
          onClick={onClose}
        />
      )}

      {/* Menu */}
      <div style={{
        position: 'fixed',
        top: 0,
        bottom: 0,
        left: isOpen ? 0 : '-280px',
        width: '280px',
        backgroundColor: 'var(--bg-secondary)',
        borderRight: '1px solid var(--border-color)',
        zIndex: 9999,
        transition: 'left 0.3s ease',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* Header row: logo + close */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 20px',
          borderBottom: '1px solid var(--border-color)',
          minHeight: '56px',
          boxSizing: 'border-box',
        }}>
          <Link to="/" onClick={onClose} style={{ display: 'flex', alignItems: 'center' }}>
            <img src={imgNexxupOfficialLogo1021} alt="Nexxup" width={98} height={22} style={{ height: 22, display: 'block' }} />
          </Link>
          <button
            onClick={onClose}
            aria-label="Close menu"
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-secondary)',
              fontSize: '22px',
              lineHeight: 1,
              cursor: 'pointer',
              padding: '4px 8px',
            }}
          >
            ✕
          </button>
        </div>

        {/* Nav links */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {NAV_LINKS.map(({ to, label, icon }) => (
            <Link
              key={to}
              to={to}
              onClick={onClose}
              style={linkStyle}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-elevated)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <NavIcon name={icon} />
              {label}
            </Link>
          ))}

          {/* Info group - tap to expand, since hover (the desktop dropdown's
              trigger) doesn't exist on a touch menu. */}
          <button
            type="button"
            onClick={() => setShowInfo((v) => !v)}
            style={{ ...linkStyle, width: '100%', background: 'none', border: 'none', borderBottom: '1px solid var(--border-color)', cursor: 'pointer', font: 'inherit', textAlign: 'left', justifyContent: 'space-between' }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <NavIcon name="info" />
              Info
            </span>
            <span style={{ fontSize: '12px', transform: showInfo ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>▼</span>
          </button>
          {showInfo && INFO_LINKS.map(({ to, label, icon }) => (
            <Link
              key={to}
              to={to}
              onClick={onClose}
              style={{ ...linkStyle, paddingLeft: '40px', fontSize: '14px' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-elevated)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <NavIcon name={icon} />
              {label}
            </Link>
          ))}

          {isAdmin && (
            <Link
              to="/admin"
              onClick={onClose}
              style={{ ...linkStyle, color: 'var(--accent)' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-elevated)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <NavIcon name="admin" />
              Admin Portal
            </Link>
          )}
        </div>

        {/* Account / login footer */}
        <div style={{ padding: '16px 20px', borderTop: '1px solid var(--border-color)' }}>
          <Link
            to={user ? getAccountPath() : '/signup'}
            onClick={onClose}
            style={{
              display: 'block',
              textAlign: 'center',
              background: 'var(--nx-primary-bg)',
              color: 'var(--nx-primary-text)',
              padding: '12px 16px',
              borderRadius: 'var(--nx-radius-pill)',
              textDecoration: 'none',
              fontSize: '15px',
              fontWeight: '600',
            }}
          >
            {user ? 'My Account' : 'Sign up'}
          </Link>
        </div>
      </div>
    </>
  );
}

export default MobileMenu;
